import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FiChevronDown, FiCheck, FiPlus, FiShield } from 'react-icons/fi';
import { useAuth } from '../AuthContext';
import notify from '../utils/notify';
import SocietyManagerModal from './SocietyManagerModal';
import './SocietySwitcher.css';

const ROLE_LABEL = { ADMIN: 'Admin', MEMBER: 'Member' };

/**
 * WHICH SOCIETY AM I IN — and how do I change it.
 *
 * The current society was a 9px subtitle at 60% white, and switching lived
 * three screens deep under three different names ("Society Profile",
 * "Manage Societies", "Society Memberships"), applied on any tap of a card
 * with no word that anything had happened. Now the name is a chip in the
 * header and on Home; with more than one society it opens one sheet, the
 * switch says "Now in {name}" and lands on Home, where the society's day is.
 *
 * With ONE society the chip is a plain label: a chevron that opens a list of
 * one is a small lie. Joining or creating another lives in the sheet's last
 * row and still on Profile.
 */
export const SocietyChip = ({ variant = 'header' }) => {
  const { currentSociety, userProfile } = useAuth();
  const [open, setOpen] = useState(false);
  if (!currentSociety) return null;
  const many = (userProfile?.societies?.length || 0) > 1;

  if (!many) {
    return (
      <span className={`soc-chip soc-chip--${variant} soc-chip--static`}>
        <FiShield className="soc-chip-icon" aria-hidden="true" />
        <span className="soc-chip-name">{currentSociety.name}</span>
      </span>
    );
  }
  return (
    <>
      <button type="button" className={`soc-chip soc-chip--${variant}`}
              onClick={(e) => { e.stopPropagation(); setOpen(true); }}
              aria-haspopup="dialog" aria-label={`Society: ${currentSociety.name}. Change society`}>
        <FiShield className="soc-chip-icon" aria-hidden="true" />
        <span className="soc-chip-name">{currentSociety.name}</span>
        <FiChevronDown className="soc-chip-chev" aria-hidden="true" />
      </button>
      {open && <SocietySheet onClose={() => setOpen(false)} />}
    </>
  );
};

const SocietySheet = ({ onClose }) => {
  const { currentSociety, userProfile, setCurrentSociety } = useAuth();
  const navigate = useNavigate();
  const [managing, setManaging] = useState(false);
  const societies = userProfile?.societies || [];

  const choose = (s) => {
    onClose();
    if (s.id === currentSociety?.id) return;
    setCurrentSociety(s);
    notify.success(`Now in ${s.name}`);
    navigate('/home');
  };

  if (managing) {
    return <SocietyManagerModal isOpen onClose={() => { setManaging(false); onClose(); }} />;
  }

  // Portalled to <body>, so it names its own font (it is outside `.App`).
  return ReactDOM.createPortal(
    <div className="soc-sheet-backdrop" onClick={onClose} role="presentation">
      <div className="soc-sheet" role="dialog" aria-modal="true" aria-labelledby="soc-sheet-title"
           onClick={(e) => e.stopPropagation()}>
        <div className="soc-sheet-handle" aria-hidden="true" />
        <h2 id="soc-sheet-title" className="soc-sheet-title">Your societies</h2>
        <ul className="soc-sheet-list">
          {societies.map((s) => {
            const current = s.id === currentSociety?.id;
            return (
              <li key={s.id}>
                <button type="button" className={`soc-row${current ? ' soc-row--current' : ''}`}
                        onClick={() => choose(s)} aria-current={current ? 'true' : undefined}>
                  <span className="soc-row-name">{s.name}</span>
                  <span className="soc-row-role">{ROLE_LABEL[s.role] || s.role}</span>
                  <span className="soc-row-check" aria-hidden="true">{current && <FiCheck size={18} />}</span>
                </button>
              </li>
            );
          })}
        </ul>
        <button type="button" className="soc-row soc-row--add" onClick={() => setManaging(true)}>
          <FiPlus size={18} aria-hidden="true" />
          <span className="soc-row-name">Join or create a society</span>
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default SocietyChip;
